import { STATIONS, SA_PROVINCES } from './stations';
import type { IDWStation } from './types';

// Plain-async Open-Meteo multi-point wind fetch. Rewritten from
// sites/my-fdi/src/api/openMeteoApi.ts with the Redux thunk + FDI/temp/RH
// machinery stripped — the frontend only needs the wind vector field.
//
// Open-Meteo is free and key-less and accepts comma-separated coordinate
// arrays, so this runs entirely client-side (no backend/Lambda involved).

interface OpenMeteoHourly {
  time: string[];
  wind_speed_10m: number[];
  wind_direction_10m: number[];
}

interface OpenMeteoResponse {
  hourly?: OpenMeteoHourly;
}

export interface WindFieldData {
  /** ISO hour timestamps shared across all stations. */
  hours: string[];
  /** Index of the hour closest to "now". */
  currentHourIndex: number;
  /** Per-hour array of station wind samples: stationsByHour[hour][station]. */
  stationsByHour: IDWStation[][];
}

/** Group station indices by country so each Open-Meteo call stays regional. */
function groupStationsByCountry(): Map<string, number[]> {
  const groups = new Map<string, number[]>();
  STATIONS.forEach((s, i) => {
    const country = SA_PROVINCES.has(s.province) ? 'ZA' : s.province;
    const arr = groups.get(country);
    if (arr) arr.push(i);
    else groups.set(country, [i]);
  });
  return groups;
}

/** Fetch hourly wind for a subset of stations directly from Open-Meteo. */
async function fetchGroup(
  indices: number[],
): Promise<{ index: number; data: OpenMeteoResponse }[]> {
  const lats = indices.map((i) => STATIONS[i]!.lat).join(',');
  const lons = indices.map((i) => STATIONS[i]!.lng).join(',');
  const url =
    `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${lats}&longitude=${lons}` +
    `&hourly=wind_speed_10m,wind_direction_10m` +
    `&wind_speed_unit=ms&timezone=Africa%2FJohannesburg&forecast_days=2`;

  const response = await fetch(url);
  if (!response.ok) throw new Error(`Open-Meteo HTTP ${response.status}`);
  const json = await response.json();
  const arr: OpenMeteoResponse[] = Array.isArray(json) ? json : [json];
  return arr.map((data, j) => ({ index: indices[j]!, data }));
}

/** Convert a station's hourly wind reading to an IDW sample. */
function toIDWStation(
  stationIndex: number,
  hourly: OpenMeteoHourly,
  hourIndex: number,
): IDWStation {
  const speedMs = hourly.wind_speed_10m[hourIndex] ?? 0;
  const dirDeg = hourly.wind_direction_10m[hourIndex] ?? 0;
  const rad = (dirDeg * Math.PI) / 180;
  const station = STATIONS[stationIndex]!;
  return {
    lat: station.lat,
    lng: station.lng,
    // Meteorological convention: direction is where wind comes FROM, so the
    // motion vector points the opposite way.
    u: -Math.sin(rad) * speedMs,
    v: -Math.cos(rad) * speedMs,
    speedKt: speedMs * 1.94384,
  };
}

/** Fetch the full multi-station hourly wind field from Open-Meteo. */
export async function fetchWindField(): Promise<WindFieldData> {
  const groups = groupStationsByCountry();
  const groupResults = await Promise.all(
    [...groups.values()].map((indices) => fetchGroup(indices)),
  );

  const responses: OpenMeteoResponse[] = new Array(STATIONS.length);
  for (const group of groupResults) {
    for (const { index, data } of group) responses[index] = data;
  }

  // Use the first station with hourly data to establish the shared timeline.
  const reference = responses.find((r) => r?.hourly)?.hourly;
  if (!reference) throw new Error('Open-Meteo returned no hourly data');

  const hours = reference.time;
  const numHours = hours.length;

  // Closest hour to now.
  const now = Date.now();
  let currentHourIndex = 0;
  let minDiff = Infinity;
  for (let h = 0; h < numHours; h++) {
    const diff = Math.abs(new Date(hours[h]!).getTime() - now);
    if (diff < minDiff) {
      minDiff = diff;
      currentHourIndex = h;
    }
  }

  const stationsByHour: IDWStation[][] = [];
  for (let h = 0; h < numHours; h++) {
    const hourSamples: IDWStation[] = [];
    responses.forEach((d, i) => {
      if (d?.hourly) hourSamples.push(toIDWStation(i, d.hourly, h));
    });
    stationsByHour.push(hourSamples);
  }

  return { hours, currentHourIndex, stationsByHour };
}
